import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  SelectChangeEvent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  PictureAsPdf as PdfIcon,
  Assessment as ReportIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import jsPDF from 'jspdf';
import api from '../services/api';
import {
  COMMON_TIMEZONES,
  getBrowserTimeZone,
  formatTimeInZone,
  formatShortDateInZone,
  formatDateTimeInZone,
  wallClockToUtcIso,
  utcIsoToWallClock,
} from '../utils/timezone';

// TypeScript Interfaces
interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  employment_type: string;
  status: string;
  organizations?: Array<{ id: number; name: string }>;
}

interface TimeEntry {
  id: number;
  user_id: number;
  clock_in: string;
  clock_out: string | null;
  clock_in_timezone?: string | null;
  clock_out_timezone?: string | null;
  duration_minutes: number | null;
  notes: string | null;
  edited_by: number | null;
  edited_by_username?: string;
}

interface EditHistory {
  id: number;
  edited_at: string;
  edited_by: number;
  edited_by_username: string;
  old_clock_in: string | null;
  new_clock_in: string;
  old_clock_out: string | null;
  new_clock_out: string | null;
  edit_reason: string;
}

interface Organization {
  id: number;
  name: string;
}

interface EditFormData {
  clockIn: string;
  clockOut: string;
  notes: string;
  editReason: string;
}

interface ManualEntryFormData {
  clockIn: string;
  clockOut: string;
  notes: string;
  reason: string;
}

// Browser timezone first, then the common US zones
const TIMEZONE_OPTIONS = Array.from(new Set([getBrowserTimeZone(), ...COMMON_TIMEZONES]));

const TimeclockAdmin: React.FC = () => {
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // Edit modal state
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [editForm, setEditForm] = useState<EditFormData>({
    clockIn: '',
    clockOut: '',
    notes: '',
    editReason: ''
  });
  const [editTimezone, setEditTimezone] = useState<string>(getBrowserTimeZone());
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  
  // Edit history modal state
  const [editHistory, setEditHistory] = useState<EditHistory[]>([]);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  
  // Create manual entry modal state
  const [showManualEntryModal, setShowManualEntryModal] = useState<boolean>(false);
  const [manualEntryForm, setManualEntryForm] = useState<ManualEntryFormData>({
    clockIn: '',
    clockOut: '',
    notes: '',
    reason: ''
  });
  const [manualTimezone, setManualTimezone] = useState<string>(getBrowserTimeZone());

  // Set default dates (Monday through Sunday of current week)
  useEffect(() => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Calculate Monday of current week
    const monday = new Date(today);
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday, go back 6 days
    monday.setDate(today.getDate() - daysFromMonday);
    
    // Calculate Sunday of current week
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    // Format as local calendar dates (NOT toISOString, which is UTC and can
    // be a day off near midnight)
    const toLocalDateStr = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setStartDate(toLocalDateStr(monday));
    setEndDate(toLocalDateStr(sunday));
  }, []);

  // Get selected organization
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);

  // Load organization from localStorage
  useEffect(() => {
    try {
      const orgStr = localStorage.getItem('selectedOrg');
      const userStr = localStorage.getItem('user');
      
      if (userStr) {
        const user = JSON.parse(userStr);
        
        // If no selectedOrg in localStorage but user has organizations, use the first one
        if (!orgStr && user.organizations && user.organizations.length > 0) {
          const org = user.organizations[0];
          setSelectedOrg(org);
          localStorage.setItem('selectedOrg', JSON.stringify(org));
        } else if (orgStr) {
          setSelectedOrg(JSON.parse(orgStr));
        }
      }
    } catch (error) {
      console.error('Error loading org/user from localStorage:', error);
    }
  }, []);

  // Fetch users in the organization
  const { data: usersData } = useQuery<{ users: User[] }>({
    queryKey: ['users', selectedOrg?.id],
    queryFn: async () => {
      if (!selectedOrg?.id) {
        throw new Error('No organization selected');
      }
      return api.getUsers(selectedOrg.id);
    },
    enabled: !!selectedOrg?.id,
  });

  // Filter to only show ACTIVE W2 employees (exclude superusers)
  const users = (usersData?.users || []).filter(u => {
    const hasOrg = u.organizations?.some(org => org.id === selectedOrg?.id);
    const isNotSuperuser = u.role !== 'superuser';
    const isW2Employee = u.employment_type === 'W2';
    const isActive = u.status === 'Active';
    return hasOrg && isNotSuperuser && isW2Employee && isActive;
  });

  // Fetch time entries for selected user
  const { data: timeEntriesData, isLoading, refetch } = useQuery<{ entries: TimeEntry[] }>({
    queryKey: ['timeEntries', selectedUser, startDate, endDate],
    queryFn: async () => {
      if (!selectedUser || !startDate || !endDate) {
        throw new Error('Please select a user and date range');
      }
      return api.getUserTimeEntries(parseInt(selectedUser), startDate, endDate);
    },
    enabled: !!selectedUser && !!startDate && !!endDate,
  });

  const timeEntries = timeEntriesData?.entries || [];

  // Edit entry mutation
  const editEntryMutation = useMutation({
    mutationFn: async ({ entryId, data }: { entryId: number; data: any }) => {
      return api.updateTimeEntry(entryId, data);
    },
    onSuccess: () => {
      setSuccess('Time entry updated successfully!');
      setShowEditModal(false);
      setEditingEntry(null);
      refetch();
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to edit entry');
    },
  });

  // Delete entry mutation
  const deleteEntryMutation = useMutation({
    mutationFn: async ({ entryId, reason }: { entryId: number; reason: string }) => {
      return api.deleteTimeEntry(entryId, reason);
    },
    onSuccess: () => {
      setSuccess('Time entry deleted successfully!');
      refetch();
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to delete entry');
    },
  });

  // Create manual entry mutation
  const createManualEntryMutation = useMutation({
    mutationFn: async (data: any) => {
      return api.createManualTimeEntry(data);
    },
    onSuccess: () => {
      setSuccess('Manual time entry created successfully!');
      setShowManualEntryModal(false);
      refetch();
      setTimeout(() => setSuccess(''), 3000);
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || 'Failed to create manual entry');
    },
  });

  const openEditModal = (entry: TimeEntry) => {
    // Show the punch times as wall-clock in the timezone they were recorded
    // in (falling back to the admin's browser timezone for legacy rows), so
    // the datetime-local inputs display the "real" local punch time.
    const zone = entry.clock_in_timezone || getBrowserTimeZone();

    setEditingEntry(entry);
    setEditTimezone(zone);
    setEditForm({
      clockIn: utcIsoToWallClock(entry.clock_in, zone),
      clockOut: entry.clock_out ? utcIsoToWallClock(entry.clock_out, entry.clock_out_timezone || zone) : '',
      notes: entry.notes || '',
      editReason: ''
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!editForm.editReason) {
      setError('Edit reason is required');
      return;
    }

    if (!editingEntry) return;

    // Convert the entered wall-clock times (in the selected timezone) to UTC
    // ISO instants for storage.
    const clockIn = wallClockToUtcIso(editForm.clockIn, editTimezone);
    const clockOut = editForm.clockOut ? wallClockToUtcIso(editForm.clockOut, editTimezone) : null;

    editEntryMutation.mutate({
      entryId: editingEntry.id,
      data: {
        clockIn,
        clockOut,
        notes: editForm.notes,
        editReason: editForm.editReason,
        clockInTimezone: editTimezone,
        clockOutTimezone: editTimezone
      }
    });
  };

  const viewEditHistory = async (entryId: number) => {
    try {
      const response = await api.getTimeEntryHistory(entryId);
      setEditHistory(response.history || []);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error fetching edit history:', error);
      setError('Failed to load edit history');
    }
  };

  const handleDeleteEntry = (entryId: number) => {
    const deleteReason = window.prompt('Please provide a reason for deleting this entry:');
    if (!deleteReason) return;

    deleteEntryMutation.mutate({ entryId, reason: deleteReason });
  };

  const formatDate = (dateString: string, timeZone?: string | null): string => {
    // Render the stored UTC instant in its recorded punch timezone; when no
    // timezone was recorded (legacy rows/history), show the viewer's local tz.
    return formatDateTimeInZone(dateString, timeZone);
  };

  const formatDuration = (minutes: number | null): string => {
    if (!minutes) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const calculateTotalHours = (): number => {
    const totalMinutes = timeEntries.reduce((sum, entry) => {
      return sum + (entry.duration_minutes || 0);
    }, 0);
    return Math.round((totalMinutes / 60) * 100) / 100;
  };

  const openManualEntryModal = () => {
    // Pre-fill with today's date in the admin's local timezone (NOT
    // toISOString, which is UTC and can be a day off near midnight)
    const zone = getBrowserTimeZone();
    const dateStr = utcIsoToWallClock(new Date().toISOString(), zone).split('T')[0];
    setManualTimezone(zone);
    setManualEntryForm({
      clockIn: `${dateStr}T09:00`,
      clockOut: `${dateStr}T17:00`,
      notes: '',
      reason: ''
    });
    setShowManualEntryModal(true);
  };

  const handleManualEntrySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!manualEntryForm.reason) {
      setError('Reason is required');
      return;
    }

    if (!selectedOrg?.id) {
      setError('No organization selected');
      return;
    }

    // Convert the entered wall-clock times (in the selected timezone) to UTC
    // ISO instants for storage
    const clockIn = wallClockToUtcIso(manualEntryForm.clockIn, manualTimezone);
    const clockOut = manualEntryForm.clockOut ? wallClockToUtcIso(manualEntryForm.clockOut, manualTimezone) : null;

    createManualEntryMutation.mutate({
      userId: parseInt(selectedUser),
      organizationId: selectedOrg.id,
      clockIn,
      clockOut,
      notes: manualEntryForm.notes,
      reason: manualEntryForm.reason,
      clockInTimezone: manualTimezone,
      clockOutTimezone: manualTimezone
    });
  };

  const generateAllUsersSummaryCSV = async () => {
    if (!startDate || !endDate) {
      setError('Please select a date range');
      return;
    }

    if (!selectedOrg?.id) {
      setError('No organization selected');
      return;
    }

    try {
      const response = await api.getAllTimeEntries(startDate, endDate, selectedOrg.id);
      const allEntries = response.entries || [];
      
      if (allEntries.length === 0) {
        setError('No time entries found for the selected date range');
        return;
      }
      
      // Group by user, then by date
      const userDailyHours: Record<string, Record<string, number>> = {};
      allEntries.forEach((entry: any) => {
        const username = entry.username;
        
        // Group by the punch's local calendar date in its recorded timezone
        const entryDate = formatShortDateInZone(entry.clock_in, entry.clock_in_timezone);
        
        if (!userDailyHours[username]) {
          userDailyHours[username] = {};
        }
        
        if (!userDailyHours[username][entryDate]) {
          userDailyHours[username][entryDate] = 0;
        }
        
        userDailyHours[username][entryDate] += entry.duration_minutes || 0;
      });
      
      // Build CSV content
      let csvContent = '';
      
      // Header
      csvContent += `SONIQ Timeclock Summary Report\n`;
      csvContent += `Organization: ${selectedOrg.name || 'Organization'}\n`;
      csvContent += `Period: ${startDate} to ${endDate}\n`;
      csvContent += `Report Generated: ${new Date().toLocaleString()}\n`;
      csvContent += `\n`;
      csvContent += `Employee,Date,Hours\n`;
      
      // Sort users alphabetically
      const sortedUsers = Object.keys(userDailyHours).sort();
      
      let grandTotalMinutes = 0;
      
      // Add data for each user
      sortedUsers.forEach((username) => {
        const dates = userDailyHours[username];
        
        // Sort dates chronologically
        const sortedDates = Object.keys(dates).sort((a, b) => {
          return new Date(a).getTime() - new Date(b).getTime();
        });
        
        let userTotalMinutes = 0;
        
        // Add each day's hours
        sortedDates.forEach((date) => {
          const minutes = dates[date];
          const hours = (minutes / 60).toFixed(2);
          userTotalMinutes += minutes;
          
          csvContent += `${username},${date},${hours}\n`;
        });
        
        // Add user total
        const userTotalHours = (userTotalMinutes / 60).toFixed(2);
        csvContent += `${username} - TOTAL,,${userTotalHours}\n`;
        csvContent += `\n`;
        
        grandTotalMinutes += userTotalMinutes;
      });
      
      // Add grand total
      const grandTotalHours = (grandTotalMinutes / 60).toFixed(2);
      csvContent += `GRAND TOTAL (ALL EMPLOYEES),,${grandTotalHours}\n`;
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `Timeclock_AllUsers_Summary_${startDate}_to_${endDate}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('All users summary CSV generated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error generating summary CSV:', error);
      setError('Failed to generate summary report');
    }
  };

  const generatePDFReport = () => {
    if (!selectedUser || timeEntries.length === 0) {
      setError('Please load time entries first');
      return;
    }

    const pdf = new jsPDF();
    const reportUserName = users.find(u => u.id === parseInt(selectedUser))?.username || '';
    
    // SONIQ Brand Colors
    const soniqBlue: [number, number, number] = [97, 171, 188];
    const superBlue: [number, number, number] = [75, 136, 162];
    const night: [number, number, number] = [12, 12, 12];
    const seasalt: [number, number, number] = [248, 249, 250];
    
    // Header with SONIQ branding
    pdf.setFillColor(...soniqBlue);
    pdf.rect(0, 0, 210, 40, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('SONIQ', 15, 20);
    
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'normal');
    pdf.text('TIMECLOCK REPORT', 15, 30);
    
    // Organization name
    pdf.setFontSize(10);
    pdf.text(selectedOrg?.name || 'Organization', 15, 36);
    
    // Report Details
    pdf.setTextColor(...night);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('EMPLOYEE:', 15, 55);
    pdf.setFont('helvetica', 'normal');
    pdf.text(reportUserName, 50, 55);
    
    pdf.setFont('helvetica', 'bold');
    pdf.text('PERIOD:', 15, 65);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${startDate} to ${endDate}`, 50, 65);
    
    pdf.setFont('helvetica', 'bold');
    pdf.text('REPORT DATE:', 15, 75);
    pdf.setFont('helvetica', 'normal');
    pdf.text(new Date().toLocaleDateString(), 50, 75);
    
    // Summary Box
    pdf.setFillColor(...seasalt);
    pdf.rect(15, 85, 180, 30, 'F');
    
    pdf.setDrawColor(...superBlue);
    pdf.setLineWidth(0.5);
    pdf.rect(15, 85, 180, 30);
    
    const totalMinutes = timeEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...superBlue);
    pdf.text('TOTAL HOURS WORKED', 105, 95, { align: 'center' });
    
    pdf.setFontSize(24);
    pdf.text(`${hours}h ${minutes}m`, 105, 108, { align: 'center' });
    
    // Time Entries Table
    pdf.setFontSize(12);
    pdf.setTextColor(...night);
    pdf.setFont('helvetica', 'bold');
    pdf.text('TIME ENTRIES DETAIL', 15, 130);
    
    // Table Header
    pdf.setFillColor(...superBlue);
    pdf.rect(15, 135, 180, 10, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('DATE', 20, 142);
    pdf.text('CLOCK IN', 60, 142);
    pdf.text('CLOCK OUT', 110, 142);
    pdf.text('HOURS', 165, 142);
    
    // Table Rows
    let yPos = 152;
    pdf.setTextColor(...night);
    pdf.setFont('helvetica', 'normal');
    
    timeEntries.forEach((entry, index) => {
      if (yPos > 270) {
        pdf.addPage();
        yPos = 20;
      }
      
      const date = formatShortDateInZone(entry.clock_in, entry.clock_in_timezone);
      const clockIn = formatTimeInZone(entry.clock_in, entry.clock_in_timezone);
      const clockOut = entry.clock_out
        ? formatTimeInZone(entry.clock_out, entry.clock_out_timezone || entry.clock_in_timezone)
        : 'Active';
      const duration = formatDuration(entry.duration_minutes);
      
      // Alternating row colors
      if (index % 2 === 0) {
        pdf.setFillColor(...seasalt);
        pdf.rect(15, yPos - 7, 180, 8, 'F');
      }
      
      pdf.text(date, 20, yPos);
      pdf.text(clockIn, 60, yPos);
      pdf.text(clockOut, 110, yPos);
      pdf.text(duration, 165, yPos);
      
      yPos += 8;
    });
    
    // Footer
    const pageCount = pdf.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(128, 128, 128);
      pdf.text(
        `Generated by SONIQ Timeclock System - Page ${i} of ${pageCount}`,
        105,
        290,
        { align: 'center' }
      );
    }
    
    // Save PDF
    const filename = `Timeclock_${reportUserName}_${startDate}_to_${endDate}.pdf`;
    pdf.save(filename);
    setSuccess('PDF report generated successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleUserChange = (event: SelectChangeEvent<string>) => {
    setSelectedUser(event.target.value);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Timeclock Admin Dashboard
      </Typography>
      <Typography color="text.secondary" gutterBottom>
        Manage team member time entries
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUser}
                onChange={handleUserChange}
                label="Select User"
              >
                <MenuItem value="">-- Select a user --</MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id.toString()}>
                    {u.username} ({u.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => refetch()}
              disabled={isLoading || !selectedUser}
            >
              {isLoading ? 'Loading...' : 'Load Entries'}
            </Button>
          </Grid>

          <Grid size={{ xs: 12, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openManualEntryModal}
              disabled={!selectedUser}
            >
              Create Manual Entry
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Report Generation Buttons */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              startIcon={<ReportIcon />}
              onClick={generateAllUsersSummaryCSV}
              disabled={!startDate || !endDate}
            >
              Generate All Users Summary (CSV)
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              startIcon={<PdfIcon />}
              onClick={generatePDFReport}
              disabled={!selectedUser || timeEntries.length === 0}
            >
              Generate PDF Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Card */}
      {selectedUser && timeEntries.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Time Entries for {users.find(u => u.id === parseInt(selectedUser))?.username}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography color="text.secondary">Date Range:</Typography>
                <Typography variant="h6">{startDate} to {endDate}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography color="text.secondary">Total Hours:</Typography>
                <Typography variant="h6" color="primary">
                  {calculateTotalHours()}h
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography color="text.secondary">Total Entries:</Typography>
                <Typography variant="h6">{timeEntries.length}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Time Entries Table */}
      {selectedUser && timeEntries.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Clock In</TableCell>
                <TableCell>Clock Out</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timeEntries.map((entry) => {
                return (
                  <TableRow
                    key={entry.id}
                    sx={{ backgroundColor: entry.edited_by ? 'rgba(255, 152, 0, 0.08)' : 'transparent' }}
                  >
                    <TableCell>{formatShortDateInZone(entry.clock_in, entry.clock_in_timezone)}</TableCell>
                    <TableCell>{formatTimeInZone(entry.clock_in, entry.clock_in_timezone)}</TableCell>
                    <TableCell>
                      {entry.clock_out
                        ? formatTimeInZone(entry.clock_out, entry.clock_out_timezone || entry.clock_in_timezone)
                        : 'Active'}
                    </TableCell>
                    <TableCell>{formatDuration(entry.duration_minutes)}</TableCell>
                    <TableCell>
                      <Box>
                        {entry.notes}
                        {entry.edited_by && (
                          <Chip
                            label={`Modified by ${entry.edited_by_username}`}
                            size="small"
                            icon={<EditIcon />}
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openEditModal(entry)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => viewEditHistory(entry.id)}
                          title="View History"
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteEntry(entry.id)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedUser && timeEntries.length === 0 && !isLoading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No time entries found for the selected user and date range.
          </Typography>
        </Paper>
      )}

      {isLoading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Edit Modal */}
      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Time Entry</DialogTitle>
        <form onSubmit={handleEditSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Clock In"
                  value={editForm.clockIn}
                  onChange={(e) => setEditForm({ ...editForm, clockIn: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Clock Out"
                  value={editForm.clockOut}
                  onChange={(e) => setEditForm({ ...editForm, clockOut: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Timezone of entered times</InputLabel>
                  <Select
                    value={editTimezone}
                    label="Timezone of entered times"
                    onChange={(e) => setEditTimezone(e.target.value)}
                  >
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <MenuItem key={tz} value={tz}>{tz.replace('_', ' ')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Edit Reason (required)"
                  multiline
                  rows={2}
                  value={editForm.editReason}
                  onChange={(e) => setEditForm({ ...editForm, editReason: e.target.value })}
                  required
                  placeholder="Explain why this entry is being edited..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowEditModal(false)} disabled={editEntryMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={editEntryMutation.isPending}>
              {editEntryMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Manual Entry Modal */}
      <Dialog open={showManualEntryModal} onClose={() => setShowManualEntryModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Manual Time Entry</DialogTitle>
        <form onSubmit={handleManualEntrySubmit}>
          <DialogContent>
            <Typography color="text.secondary" gutterBottom>
              Create a time entry for {users.find(u => u.id === parseInt(selectedUser))?.username}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Clock In"
                  value={manualEntryForm.clockIn}
                  onChange={(e) => setManualEntryForm({ ...manualEntryForm, clockIn: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Clock Out"
                  value={manualEntryForm.clockOut}
                  onChange={(e) => setManualEntryForm({ ...manualEntryForm, clockOut: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave blank to create an active entry"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Timezone of entered times</InputLabel>
                  <Select
                    value={manualTimezone}
                    label="Timezone of entered times"
                    onChange={(e) => setManualTimezone(e.target.value)}
                  >
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <MenuItem key={tz} value={tz}>{tz.replace('_', ' ')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={manualEntryForm.notes}
                  onChange={(e) => setManualEntryForm({ ...manualEntryForm, notes: e.target.value })}
                  placeholder="Optional notes about this entry..."
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Reason (required)"
                  multiline
                  rows={2}
                  value={manualEntryForm.reason}
                  onChange={(e) => setManualEntryForm({ ...manualEntryForm, reason: e.target.value })}
                  required
                  placeholder="Why are you creating this manual entry? (e.g., User forgot to clock in/out)"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowManualEntryModal(false)} disabled={createManualEntryMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={createManualEntryMutation.isPending}>
              {createManualEntryMutation.isPending ? 'Creating...' : 'Create Entry'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit History Modal */}
      <Dialog open={showHistoryModal} onClose={() => setShowHistoryModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit History</DialogTitle>
        <DialogContent>
          {editHistory.length === 0 ? (
            <Typography>No edit history available.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Edited By</TableCell>
                    <TableCell>Changes</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editHistory.map((edit) => (
                    <TableRow key={edit.id}>
                      <TableCell>{formatDate(edit.edited_at)}</TableCell>
                      <TableCell>{edit.edited_by_username}</TableCell>
                      <TableCell>
                        <Box>
                          {edit.old_clock_in && edit.new_clock_in && edit.old_clock_in !== edit.new_clock_in && (
                            <Typography variant="body2">
                              Clock In: {formatDate(edit.old_clock_in)} → {formatDate(edit.new_clock_in)}
                            </Typography>
                          )}
                          {!edit.old_clock_in && edit.new_clock_in && (
                            <Typography variant="body2">
                              Clock In: {formatDate(edit.new_clock_in)}
                            </Typography>
                          )}
                          {edit.old_clock_out !== edit.new_clock_out && (
                            <Typography variant="body2">
                              Clock Out: {edit.old_clock_out ? formatDate(edit.old_clock_out) : 'None'} → 
                              {edit.new_clock_out ? formatDate(edit.new_clock_out) : 'None'}
                            </Typography>
                          )}
                          {!edit.old_clock_out && !edit.new_clock_out && edit.new_clock_in && (
                            <Typography variant="body2">Entry created</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{edit.edit_reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoryModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TimeclockAdmin;
